import { useState, useEffect } from "react";
import {
  X,
  Building2,
  MapPin,
  Calendar,
  Users,
  DollarSign,
  Briefcase,
  Clock,
  FileText,
  Eye,
  UserCheck,
  MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { recruitmentAPI } from "@/lib/api";

const JobOfferDetails = ({ jobId, onClose }) => {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchJobDetails();
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      const response = await recruitmentAPI.getJobById(jobId);
      setJob(response.data.data);
    } catch (error) {
      console.error("Error fetching job details:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les détails de l'offre",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      brouillon: "bg-gray-100 text-gray-800",
      ouverte: "bg-green-100 text-green-800",
      fermee: "bg-red-100 text-red-800",
      suspendue: "bg-yellow-100 text-yellow-800",
      recue: "bg-blue-100 text-blue-800",
      preselectionne: "bg-yellow-100 text-yellow-800",
      entretien: "bg-purple-100 text-purple-800",
      acceptee: "bg-green-100 text-green-800",
      refusee: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getStatusLabel = (status) => {
    const labels = {
      brouillon: "Brouillon",
      ouverte: "Ouverte",
      fermee: "Fermée",
      suspendue: "Suspendue",
      recue: "Reçue",
      preselectionne: "Présélectionné",
      entretien: "Entretien",
      acceptee: "Acceptée",
      refusee: "Refusée",
    };
    return labels[status] || status;
  };

  const getContractTypeLabel = (type) => {
    const labels = {
      cdi: "CDI",
      cdd: "CDD",
      stage: "Stage",
      interim: "Intérim",
      freelance: "Freelance",
      apprentissage: "Apprentissage",
    };
    return labels[type] || type;
  };

  const getInterviewTypeLabel = (type) => {
    const labels = {
      telephonique: "Téléphonique",
      visio: "Visioconférence",
      presentiel: "Présentiel",
      technique: "Technique",
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="text-center py-8">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Offre d'emploi non trouvée</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <Briefcase className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{job.titre}</h2>
            <div className="flex items-center gap-3 mt-2">
              <Badge className={getStatusColor(job.statut)}>
                {getStatusLabel(job.statut)}
              </Badge>
              {job.type_contrat && (
                <Badge variant="outline">
                  {getContractTypeLabel(job.type_contrat)}
                </Badge>
              )}
            </div>
          </div>
        </div>

        <Button variant="outline" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      {/* Quick Info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {job.departement && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Building2 className="h-4 w-4" />
            <span>{job.departement.nom}</span>
          </div>
        )}

        {job.lieu_travail && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <MapPin className="h-4 w-4" />
            <span>{job.lieu_travail}</span>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="h-4 w-4" />
          <span>
            Créée le {new Date(job.date_creation).toLocaleDateString("fr-FR")}
          </span>
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Users className="h-4 w-4" />
          <span>{job.candidatures?.length || 0} candidature(s)</span>
        </div>
      </div>

      <Separator />

      <Tabs defaultValue="details" className="space-y-6">
        <TabsList>
          <TabsTrigger value="details">Détails</TabsTrigger>
          <TabsTrigger value="applications">
            Candidatures ({job.candidatures?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Details */}
            <div className="lg:col-span-2 space-y-6">
              {job.description && (
                <Card>
                  <CardHeader>
                    <CardTitle>Description du poste</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose max-w-none">
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {job.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {job.experience_requise && (
                <Card>
                  <CardHeader>
                    <CardTitle>Expérience requise</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {job.experience_requise}
                    </p>
                  </CardContent>
                </Card>
              )}

              {job.competences_requises && (
                <Card>
                  <CardHeader>
                    <CardTitle>Compétences et qualifications</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {job.competences_requises}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Job Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Informations</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(job.salaire_min || job.salaire_max) && (
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium">Salaire</p>
                        <p className="text-sm text-gray-600">
                          {job.salaire_min && job.salaire_max
                            ? `${job.salaire_min.toLocaleString()} - ${job.salaire_max.toLocaleString()} MAD`
                            : job.salaire_min
                            ? `À partir de ${job.salaire_min.toLocaleString()} MAD`
                            : `Jusqu'à ${job.salaire_max.toLocaleString()} MAD`}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium">Postes disponibles</p>
                      <p className="text-sm text-gray-600">
                        {job.nombre_postes || 1}
                      </p>
                    </div>
                  </div>

                  {job.date_publication && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium">
                          Date de publication
                        </p>
                        <p className="text-sm text-gray-600">
                          {new Date(job.date_publication).toLocaleDateString(
                            "fr-FR"
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  {job.date_fin && (
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium">Date limite</p>
                        <p className="text-sm text-gray-600">
                          {new Date(job.date_fin).toLocaleDateString("fr-FR")}
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Publisher Info */}
              {job.publieur && (
                <Card>
                  <CardHeader>
                    <CardTitle>Publié par</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <UserCheck className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {job.publieur.prenom} {job.publieur.nom}
                        </p>
                        <p className="text-sm text-gray-600">Responsable RH</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="applications" className="space-y-6">
          {job.candidatures && job.candidatures.length > 0 ? (
            <div className="space-y-4">
              {job.candidatures.map((application) => (
                <Card key={application.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <UserCheck className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">
                            {application.prenom} {application.nom}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {application.email}
                          </p>
                          {application.telephone && (
                            <p className="text-sm text-gray-600">
                              {application.telephone}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            Candidature reçue le{" "}
                            {new Date(
                              application.date_candidature
                            ).toLocaleDateString("fr-FR")}
                          </p>
                        </div>
                      </div>

                      <div className="text-right">
                        <Badge className={getStatusColor(application.statut)}>
                          {getStatusLabel(application.statut)}
                        </Badge>

                        {application.note_evaluation && (
                          <p className="text-sm text-gray-600 mt-2">
                            Note: {application.note_evaluation}/20
                          </p>
                        )}

                        {application.entretiens &&
                          application.entretiens.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-gray-500">
                                {application.entretiens.length} entretien(s)
                              </p>
                            </div>
                          )}
                      </div>
                    </div>

                    {application.lettre_motivation && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-sm font-medium mb-2">
                          Lettre de motivation:
                        </p>
                        <p className="text-sm text-gray-700 line-clamp-3">
                          {application.lettre_motivation}
                        </p>
                      </div>
                    )}

                    {application.commentaires && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-sm font-medium mb-2">
                          Commentaires d'évaluation:
                        </p>
                        <p className="text-sm text-gray-700">
                          {application.commentaires}
                        </p>
                        {application.evaluateur && (
                          <p className="text-xs text-gray-500 mt-1">
                            Évalué par {application.evaluateur.prenom}{" "}
                            {application.evaluateur.nom}
                          </p>
                        )}
                      </div>
                    )}

                    {application.entretiens &&
                      application.entretiens.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <p className="text-sm font-medium mb-2">
                            Entretiens:
                          </p>
                          <div className="space-y-2">
                            {application.entretiens.map((interview) => (
                              <div
                                key={interview.id}
                                className="flex items-center justify-between text-sm"
                              >
                                <div>
                                  <span className="font-medium">
                                    {getInterviewTypeLabel(
                                      interview.type_entretien
                                    )}
                                  </span>
                                  <span className="text-gray-500 ml-2">
                                    {new Date(
                                      interview.date_entretien
                                    ).toLocaleDateString("fr-FR")}
                                  </span>
                                  {interview.intervieweur && (
                                    <span className="text-gray-500 ml-2">
                                      avec {interview.intervieweur.prenom}{" "}
                                      {interview.intervieweur.nom}
                                    </span>
                                  )}
                                </div>
                                <Badge
                                  variant="outline"
                                  className={
                                    interview.resultat
                                      ? getStatusColor(interview.resultat)
                                      : ""
                                  }
                                >
                                  {interview.resultat
                                    ? getStatusLabel(interview.resultat)
                                    : getStatusLabel(interview.statut)}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">
                    Aucune candidature pour cette offre
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default JobOfferDetails;
